// MBA Lab — ROI calculator UI. Zero node_modules imports: only lib/finance.js
// (the pure engine) and lib/core.js (fmt/store/analytics). rAF-debounced live
// updates - there is no Calculate button.
import { computeRoi } from './lib/finance.js';
import { fmt, store, trackToolAction } from './lib/core.js';
import { hydrate, mountSyncIndicator, syncedStore } from './lib/account-sync.js';
import {
  parseNumber,
  formatApproxCurrency,
  formatNumberForInput,
  reformatNumberInput,
  shouldRejectTypedChar,
  breakevenHeadlineText,
  breakevenBadgeLabel,
  toEngineInputs,
  CLAMPABLE_FIELDS,
  resolveClampNote,
  niceStep,
  areaPath,
  chartAriaLabel,
  defaultIncomeDuringProgram,
  incomeDuringProgramNoteText,
} from './lib/roi-view.js';
import { initToolDock } from './lib/tool-dock.js';

const TOOL_NAME = 'mba-roi-calculator';
const MAX_SAVED_AGE_DAYS = 90;
const ANNOUNCE_DEBOUNCE_MS = 1200;

// The dollar-amount fields, formatted with comma thousands separators as the
// user types (see wireCommaFormattedFields()) - everything else (percents,
// years, months) never reaches four digits, so it doesn't need grouping.
const CURRENCY_FIELD_KEYS = ['currentSalary', 'totalCost', 'scholarships', 'incomeDuringProgram', 'postSalary'];

const FORMAT_DEFAULT_LENGTH_MONTHS = {
  MBA: 21,
  'Online MBA': 24,
  'Part-Time MBA': 32,
  'Executive MBA': 21,
};

const DEFAULT_INPUTS = {
  currentSalary: 75000,
  noMbaRaisePercent: 3,
  format: 'MBA',
  totalCost: 0,
  scholarships: 0,
  lengthMonths: 21,
  incomeDuringProgram: 0,
  percentBorrowed: 50,
  loanRatePercent: 8,
  loanTermYears: 10,
  postMbaSalary: 75000,
  postMbaRaisePercent: 5,
  discountRatePercent: 5,
};

function byId(id) {
  return document.getElementById(id);
}

class RoiCalculator {
  constructor(root) {
    this.form = byId('roi-form');
    this.els = {
      currentSalary: byId('roi-current-salary'),
      noMbaRaise: byId('roi-no-mba-raise'),
      format: byId('roi-format'),
      totalCost: byId('roi-total-cost'),
      costEstimateNote: byId('roi-cost-estimate-note'),
      residencyWrap: byId('roi-residency-wrap'),
      scholarships: byId('roi-scholarships'),
      lengthMonths: byId('roi-length-months'),
      incomeDuringProgram: byId('roi-income-during-program'),
      incomeNote: byId('roi-income-note'),
      percentBorrowed: byId('roi-percent-borrowed'),
      loanRate: byId('roi-loan-rate'),
      loanTermYears: byId('roi-loan-term-years'),
      postSalary: byId('roi-post-salary'),
      salaryPresets: byId('roi-salary-presets'),
      earningsChip: byId('roi-earnings-chip'),
      earningsChipNote: byId('roi-earnings-chip-note'),
      postRaise: byId('roi-post-raise'),
      discountRate: byId('roi-discount-rate'),
      reset: byId('roi-reset'),

      noMbaRaiseNote: byId('roi-no-mba-raise-note'),
      lengthMonthsNote: byId('roi-length-months-note'),
      percentBorrowedNote: byId('roi-percent-borrowed-note'),
      loanRateNote: byId('roi-loan-rate-note'),
      loanTermYearsNote: byId('roi-loan-term-years-note'),
      postRaiseNote: byId('roi-post-raise-note'),
      discountRateNote: byId('roi-discount-rate-note'),

      breakevenValue: byId('roi-breakeven-value'),
      breakevenAnnouncer: byId('roi-breakeven-announcer'),
      chart: byId('roi-chart'),
      netCost: byId('roi-net-cost'),
      interest: byId('roi-interest'),
      foregone: byId('roi-foregone'),
      totalInvestment: byId('roi-total-investment'),
      sensitivityRows: byId('roi-sensitivity-rows'),
      assumptionsRecap: byId('roi-assumptions-recap'),
    };

    this.dock = initToolDock({ dock: byId('roi-dock'), jumpTarget: byId('roi-results') });

    this.prefill = this.readPrefill();
    // Signed in: also syncs to the account (see lib/account-sync.js). Signed out, or with the
    // feature off, this is a pure passthrough to the same localStorage wrapper.
    // Version 1 - this tool predates the models' schema/version envelope; see the bootstrap below.
    this.roiStore = syncedStore(TOOL_NAME, store(TOOL_NAME), {
      schemaVersion: 1,
      onStatus: mountSyncIndicator(),
    });
    this.rafId = null;
    this.announceTimer = null;
    this.lengthManuallyEdited = false;
    this.incomeManuallyEdited = false;
    this.hasEngaged = false;
    this.hasRenderedOnce = false;
    this.hasFiredResult = false;

    this.applyInitialInputs();
    this.wireEvents();
    trackToolAction(TOOL_NAME, 'open');
    this.update();
  }

  readPrefill() {
    const island = byId('roi-prefill');
    if (!island) return {};
    try {
      return JSON.parse(island.textContent) || {};
    } catch {
      return {};
    }
  }

  /** Returns the full saved record (inputs + the manual-edit flags below),
   * or null - distinct from just the inputs, since applyInitialInputs needs
   * the flags too. */
  loadSaved() {
    const saved = this.roiStore.load();
    if (!saved || !saved.savedAt || !saved.inputs) return null;
    const ageDays = (Date.now() - saved.savedAt) / 86400000;
    return ageDays <= MAX_SAVED_AGE_DAYS ? saved : null;
  }

  persist(inputs) {
    this.roiStore.save({
      savedAt: Date.now(),
      inputs,
      // Persisted alongside the raw values, not derived from them - the
      // manual-edit flags carry meaning ("did a person choose this, or is it
      // just sitting at whatever the last computed default was") that the
      // saved *value* alone can't recover across a reload.
      lengthManuallyEdited: this.lengthManuallyEdited,
      incomeManuallyEdited: this.incomeManuallyEdited,
    });
  }

  /** Precedence: URL prefill > saved state (<90 days) > defaults. */
  applyInitialInputs(skipSaved) {
    const saved = skipSaved ? null : this.loadSaved();
    const savedInputs = saved?.inputs;
    const merged = { ...DEFAULT_INPUTS, ...(savedInputs || {}) };

    // Pre-existing gap found while verifying today's changes, not part of
    // them: RoiToolService.BuildViewModel's Format is a non-nullable
    // `required string` that always resolves to at least "MBA" - the prefill
    // JSON island therefore always carries a truthy `format` (and a non-null
    // `lengthMonths`) even on a bare URL with zero real school/program
    // context, which meant format/length silently reset on *every* reload
    // regardless of what was saved. `school` is only ever non-null when a
    // real College actually resolved server-side, so it's the correct signal
    // for "this URL genuinely carries prefill data" - everything else in
    // Model only gets meaningfully set once a school does.
    const hasRealPrefill = this.prefill.school != null;
    if (hasRealPrefill && this.prefill.format) merged.format = this.prefill.format;
    if (hasRealPrefill && this.prefill.lengthMonths != null) merged.lengthMonths = this.prefill.lengthMonths;
    // totalCost is a genuinely nullable Model.TotalCost server-side (only
    // ever set once a Program resolves, which requires school), so it was
    // never actually reachable with a real value while hasRealPrefill is
    // false - the guard is added anyway for the same explicit invariant the
    // two lines above now state, not because it changes behavior today.
    if (hasRealPrefill && this.prefill.totalCost != null) merged.totalCost = this.prefill.totalCost;
    // Recomputed fresh whenever saved state doesn't reflect a genuine manual
    // edit - "some value is sitting in storage" is not the same as "a person
    // chose it": update() persists on every render, including the very first
    // one with zero interaction, so a bare visit alone is enough to save
    // whatever the format-driven default happened to be. Gating on the
    // manually-edited flag (persisted above) instead of merely "saved state
    // exists" means that stale, never-touched value can't freeze in place
    // when a fresh college cross-link changes format/salary/length under it.
    if (!saved?.incomeManuallyEdited) {
      merged.incomeDuringProgram = defaultIncomeDuringProgram(merged.format, merged.currentSalary, merged.lengthMonths);
    }

    this.setFormValues(merged);
    this.lengthManuallyEdited = saved?.lengthManuallyEdited ?? false;
    this.incomeManuallyEdited = saved?.incomeManuallyEdited ?? false;
    this.syncFormatDependentUi();
  }

  setFormValues(v) {
    this.els.currentSalary.value = formatNumberForInput(v.currentSalary);
    this.els.noMbaRaise.value = v.noMbaRaisePercent;
    this.els.format.value = v.format;
    this.els.totalCost.value = formatNumberForInput(v.totalCost);
    this.els.scholarships.value = formatNumberForInput(v.scholarships);
    this.els.lengthMonths.value = v.lengthMonths;
    this.els.incomeDuringProgram.value = formatNumberForInput(v.incomeDuringProgram);
    this.els.percentBorrowed.value = v.percentBorrowed;
    this.els.loanRate.value = v.loanRatePercent;
    this.els.loanTermYears.value = v.loanTermYears;
    this.els.postSalary.value = formatNumberForInput(v.postMbaSalary);
    this.els.postRaise.value = v.postMbaRaisePercent;
    this.els.discountRate.value = v.discountRatePercent;
  }

  readFormValues() {
    return {
      currentSalary: parseNumber(this.els.currentSalary.value),
      noMbaRaisePercent: parseNumber(this.els.noMbaRaise.value),
      format: this.els.format.value,
      totalCost: parseNumber(this.els.totalCost.value),
      scholarships: parseNumber(this.els.scholarships.value),
      lengthMonths: parseNumber(this.els.lengthMonths.value),
      incomeDuringProgram: parseNumber(this.els.incomeDuringProgram.value),
      percentBorrowed: parseNumber(this.els.percentBorrowed.value),
      loanRatePercent: parseNumber(this.els.loanRate.value),
      loanTermYears: parseNumber(this.els.loanTermYears.value),
      postMbaSalary: parseNumber(this.els.postSalary.value),
      postMbaRaisePercent: parseNumber(this.els.postRaise.value),
      discountRatePercent: parseNumber(this.els.discountRate.value),
    };
  }

  syncFormatDependentUi() {
    const format = this.els.format.value;
    this.els.incomeNote.textContent = incomeDuringProgramNoteText(format);

    // The prefilled per-credit rates belong to one specific program - once
    // the user switches Format away from that program's own format, they no
    // longer apply (a different program's per-credit numbers must not leak
    // into a newly-selected format).
    const prefillStillApplies = this.prefill.program && format === this.prefill.format;
    const hasOutOfState = prefillStillApplies && this.prefill.tuitionPerCreditOutOfState != null
      && this.prefill.creditHours != null;
    this.els.residencyWrap.hidden = !hasOutOfState;

    this.els.costEstimateNote.hidden = !this.prefill.costIsEstimated;

    const hasEarnings = this.prefill.earnings1Yr != null;
    this.els.earningsChip.hidden = !hasEarnings;
    this.els.earningsChipNote.hidden = !hasEarnings;
    if (hasEarnings) {
      this.els.earningsChip.textContent = `Use federal 52.02 earnings reference (${fmt.currency(this.prefill.earnings1Yr)})`;
    }
  }

  /** Live-reformats the dollar-amount fields with comma thousands separators
   * as the user types, preserving cursor position - see
   * reformatNumberInput()'s doc comment in lib/roi-view.js. Runs as its own
   * listener (registered before the generic form-level one below) so the
   * field's .value is already comma-formatted by the time readFormValues()
   * runs; parseNumber() strips the commas back out when reading it. A
   * `beforeinput` guard rejects a typed non-digit character outright (it
   * never reaches the field at all, not even for an instant) while still
   * letting a multi-character paste through for the `input` handler to clean
   * up - see shouldRejectTypedChar()'s doc comment for why those two cases
   * are handled differently. */
  wireCommaFormattedFields() {
    for (const key of CURRENCY_FIELD_KEYS) {
      const el = this.els[key];
      el.addEventListener('beforeinput', (e) => {
        if (shouldRejectTypedChar(e.data)) e.preventDefault();
      });
      el.addEventListener('input', () => {
        const { formatted, cursorPos } = reformatNumberInput(el.value, el.selectionStart);
        el.value = formatted;
        el.setSelectionRange(cursorPos, cursorPos);
      });
    }
  }

  wireEvents() {
    this.wireCommaFormattedFields();

    this.form.addEventListener('input', (e) => {
      if (e.target === this.els.lengthMonths) this.lengthManuallyEdited = true;
      if (e.target === this.els.incomeDuringProgram) this.incomeManuallyEdited = true;
      // The Format dropdown isn't the only thing the income default depends
      // on - editing salary or length after a format-driven auto-fill left
      // it stale otherwise (order-dependent: pick a format, then type your
      // real salary, and the income field silently kept reflecting the
      // default salary instead). Never touches the field once the user has
      // typed into it directly (the check above already flips
      // incomeManuallyEdited on that same event before this runs).
      if (!this.incomeManuallyEdited && (e.target === this.els.currentSalary || e.target === this.els.lengthMonths)) {
        this.els.incomeDuringProgram.value = formatNumberForInput(
          defaultIncomeDuringProgram(this.els.format.value, parseNumber(this.els.currentSalary.value), parseNumber(this.els.lengthMonths.value))
        );
      }
      this.markEngaged();
      this.scheduleUpdate();
    });

    this.els.format.addEventListener('change', () => {
      const format = this.els.format.value;
      // A program-specific prefilled length (e.g. a real 20-month Executive
      // MBA) must survive switching Format away and back - falling back to
      // the generic per-format default here would silently replace it with
      // e.g. Executive MBA's generic 21 months, which then also throws off
      // the income default computed from it. Mirrors the same
      // prefillStillApplies check the residency-toggle handler below already
      // uses for the identical reason.
      const prefillStillApplies = this.prefill.program && format === this.prefill.format;
      if (!this.lengthManuallyEdited) {
        this.els.lengthMonths.value = prefillStillApplies && this.prefill.lengthMonths != null
          ? this.prefill.lengthMonths
          : (FORMAT_DEFAULT_LENGTH_MONTHS[format] || 21);
      }
      if (!this.incomeManuallyEdited) {
        this.els.incomeDuringProgram.value = formatNumberForInput(
          defaultIncomeDuringProgram(format, parseNumber(this.els.currentSalary.value), parseNumber(this.els.lengthMonths.value))
        );
      }
      this.syncFormatDependentUi();
      this.scheduleUpdate();
    });

    this.form.querySelectorAll('input[name="roi-residency"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        const prefillStillApplies = this.prefill.program && this.els.format.value === this.prefill.format;
        if (!prefillStillApplies || this.prefill.tuitionPerCredit == null || this.prefill.creditHours == null) return;
        const rate = radio.value === 'out-of-state'
          ? (this.prefill.tuitionPerCreditOutOfState ?? this.prefill.tuitionPerCredit)
          : this.prefill.tuitionPerCredit;
        this.els.totalCost.value = formatNumberForInput(rate * this.prefill.creditHours);
        this.scheduleUpdate();
      });
    });

    this.els.salaryPresets.addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      if (!btn) return;
      if (btn.id === 'roi-earnings-chip') {
        this.els.postSalary.value = formatNumberForInput(this.prefill.earnings1Yr);
      } else if (btn.dataset.preset) {
        this.els.postSalary.value = formatNumberForInput(Number(btn.dataset.preset));
      } else {
        return;
      }
      this.markEngaged();
      this.scheduleUpdate();
    });

    this.els.reset.addEventListener('click', () => {
      this.roiStore.deleteAccountState();
      this.applyInitialInputs(/* skipSaved */ true);
      this.hasFiredResult = false;
      this.update();
    });

    document.querySelectorAll('.content-page .dir-card.tool-card').forEach((link) => {
      link.addEventListener('click', () => trackToolAction(TOOL_NAME, 'crosslink_click', { href: link.getAttribute('href') }));
    });
  }

  markEngaged() {
    if (this.hasEngaged) return;
    this.hasEngaged = true;
    trackToolAction(TOOL_NAME, 'engage');
  }

  scheduleUpdate() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.update();
    });
  }

  update() {
    const values = this.readFormValues();
    this.persist(values);
    const roi = computeRoi(toEngineInputs(values));
    this.render(values, roi);

    if (this.hasFiredResult) {
      trackToolAction(TOOL_NAME, 'result', { breakeven_status: roi.breakeven.status });
    }
    this.hasFiredResult = true;
  }

  render(values, roi) {
    const headline = breakevenHeadlineText(roi.breakeven);
    this.els.breakevenValue.textContent = headline;
    this.scheduleAnnouncement(headline);
    this.dock?.setReading(headline);

    this.els.netCost.textContent = formatApproxCurrency(roi.netProgramCost);
    this.els.interest.textContent = formatApproxCurrency(roi.financingInterest);
    this.els.foregone.textContent = formatApproxCurrency(roi.foregoneEarnings);
    this.els.totalInvestment.textContent = formatApproxCurrency(roi.totalEconomicInvestment);

    this.renderSensitivity(roi.sensitivity);
    this.renderAssumptionsRecap(roi);
    this.renderChart(roi);
    this.updateClampNotes(values, roi.inputs);
    this.updateSalaryPresetActiveState(roi.inputs.postMbaSalary);

    if (!this.hasRenderedOnce) {
      this.hasRenderedOnce = true;
      this.els.chart.classList.remove('tool-roi-chart--loading');
    }
  }

  /**
   * The visible headline updates instantly on every keystroke (rAF-debounced
   * via scheduleUpdate); a screen reader announcing that same rate would be
   * unusable. This mirrors the same text into a separate visually-hidden
   * aria-live region, but only once input has paused - restrained, not silent.
   */
  scheduleAnnouncement(text) {
    if (this.announceTimer) clearTimeout(this.announceTimer);
    this.announceTimer = setTimeout(() => {
      this.announceTimer = null;
      if (this.els.breakevenAnnouncer.textContent !== text) {
        this.els.breakevenAnnouncer.textContent = text;
      }
    }, ANNOUNCE_DEBOUNCE_MS);
  }

  /** Surfaces a note next to any field normalizeInputs silently clamped or
   * rounded, so a typo like "1.4" in a whole-years field doesn't just change
   * the results with no explanation - see resolveClampNote() in lib/roi-view.js. */
  updateClampNotes(values, normalizedInputs) {
    for (const field of CLAMPABLE_FIELDS) {
      const { isClamped, text } = resolveClampNote(values[field.formKey], normalizedInputs, field);
      const note = this.els[field.noteEl];
      note.hidden = !isClamped;
      if (isClamped) note.textContent = text;
    }
  }

  updateSalaryPresetActiveState(currentPostMbaSalary) {
    this.els.salaryPresets.querySelectorAll('.tool-roi-chip-btn').forEach((btn) => {
      const target = btn.id === 'roi-earnings-chip' ? this.prefill.earnings1Yr : parseFloat(btn.dataset.preset);
      btn.classList.toggle('is-active', target != null && Number.isFinite(target) && target === currentPostMbaSalary);
    });
  }

  renderSensitivity(sensitivity) {
    const container = this.els.sensitivityRows;
    container.textContent = '';
    for (const s of sensitivity) {
      const row = document.createElement('div');
      row.className = 'tool-roi-sensitivity-row' + (s.multiplier === 1 ? ' is-current' : '');

      const label = document.createElement('span');
      label.className = 'tool-roi-sensitivity-multiplier';
      label.textContent = `${Math.round(s.multiplier * 100)}% of entered salary`;

      const value = document.createElement('span');
      value.className = 'tool-roi-sensitivity-value';
      value.textContent = breakevenHeadlineText(s.breakeven);

      row.append(label, value);
      container.appendChild(row);
    }
  }

  renderAssumptionsRecap(roi) {
    const n = roi.inputs;
    this.els.assumptionsRecap.textContent =
      `Assumes a ${Math.round(n.noMbaRaiseRate * 100)}% annual raise on your current path and a ` +
      `${Math.round(n.postMbaRaiseRate * 100)}% annual raise after the MBA.`;
  }

  renderChart(roi) {
    const svg = this.els.chart;
    const months = roi.months;
    const width = 640;
    const height = 280;
    const padding = { top: 18, right: 16, bottom: 26, left: 16 };
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;
    const zeroBaselineY = padding.top + plotHeight / 2;
    const halfHeight = plotHeight / 2 - 10;

    const maxAbs = Math.max(1, ...months.map((m) => Math.abs(m.cumulativeDiscounted)));
    const step = niceStep(maxAbs);
    const axisMax = step * Math.ceil(maxAbs / step);

    const xScale = (i) => padding.left + (i / (months.length - 1)) * plotWidth;
    const yScale = (v) => zeroBaselineY - (v / axisMax) * halfHeight;

    const parts = [];

    // Program-phase tint band.
    const programEndX = xScale(Math.min(roi.inputs.lengthMonths, months.length - 1));
    parts.push(
      `<rect x="${padding.left}" y="${padding.top}" width="${Math.max(0, programEndX - padding.left)}" ` +
      `height="${plotHeight}" fill="var(--tint)" />`
    );

    // Gridlines at +/- step and 0.
    for (let v = -axisMax; v <= axisMax + 0.01; v += step) {
      const y = yScale(v);
      const emphasized = Math.abs(v) < step / 2;
      parts.push(
        `<line x1="${padding.left}" y1="${y}" x2="${width - padding.right}" y2="${y}" ` +
        `stroke="${emphasized ? 'var(--hairline-2)' : 'var(--hairline)'}" stroke-width="1" />`
      );
      parts.push(
        `<text x="${padding.left}" y="${y - 4}" font-size="10" fill="var(--faint)">${fmt.currency(v)}</text>`
      );
    }

    // Zero-clipped area fills (positive above the baseline, negative below).
    parts.push(areaPath(months, xScale, yScale, zeroBaselineY, true, 'var(--accent)'));
    parts.push(areaPath(months, xScale, yScale, zeroBaselineY, false, 'var(--ink)'));

    // The line itself.
    const points = months.map((m, i) => `${xScale(i)},${yScale(m.cumulativeDiscounted)}`).join(' ');
    parts.push(`<polyline points="${points}" fill="none" stroke="var(--accent)" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />`);

    // Breakeven marker.
    if (roi.breakeven.status === 'reached') {
      const bx = xScale(roi.breakeven.month);
      const by = yScale(0);
      parts.push(`<circle cx="${bx}" cy="${by}" r="4" fill="var(--accent)" stroke="var(--surface)" stroke-width="2" />`);
      const label = breakevenBadgeLabel(roi.breakeven.years);
      const labelWidth = 14 + label.length * 6.2;
      const labelX = Math.min(bx + 8, width - padding.right - labelWidth);
      parts.push(
        `<rect x="${labelX}" y="${by - 30}" width="${labelWidth}" height="20" rx="10" fill="var(--ink)" />` +
        `<text x="${labelX + labelWidth / 2}" y="${by - 16}" font-size="11" font-weight="600" fill="var(--surface)" text-anchor="middle">${label}</text>`
      );
    } else if (roi.breakeven.status === 'immediate') {
      parts.push(
        `<circle cx="${xScale(0)}" cy="${yScale(0)}" r="4" fill="var(--accent)" stroke="var(--surface)" stroke-width="2" />`
      );
    }

    svg.innerHTML = parts.join('');
    svg.setAttribute('aria-label', chartAriaLabel(roi));
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  const form = byId('roi-form');
  if (!form) return;
  byId('roi-chart').classList.add('tool-roi-chart--loading');
  // Reconcile with the account BEFORE the tool reads its own storage, so it simply loads whatever
  // won. A no-op (and no request) when signed out, and bounded by its own short timeout so a slow
  // network delays startup rather than blocking it.
  //
  // Version 1 because this tool predates the models' schema/version envelope - its saved shape is
  // {savedAt, inputs, flags} and has never been versioned. The server only compares the number.
  await hydrate(TOOL_NAME, store(TOOL_NAME), 1);
  new RoiCalculator();
});
